import type { TFunction } from 'i18next';
import { translateBetMarketName } from 'helpers/betI18n';
import { marketGroupOf } from 'helpers/marketGroups';
import type { MarketCard } from 'helpers/marketLines';
import type { Bet } from 'types/Bet';
import type { LiveEventBet } from 'types/LiveEvent';

/**
 * Finding a market in a match's offer by typing part of its name: "hend"
 * keeps the handicaps, "gol" the goal markets, "korn" the corners.
 *
 * A market is matched on its name as the player reads it, the feed's English
 * name, its line ("(2.5)", "(0:1)") and the name of the section it sits in, so
 * "polu" finds every half-time market. Case and diacritics do not matter
 * ("sut" finds "šut"), and every word typed must match: "gol polu" keeps the
 * goal markets of the halves.
 */

/** Lower case without diacritics: "Više/manje" -> "vise/manje". */
export const foldSearchText = (s: string) =>
  s.toLowerCase().replace(/đ/g, 'dj').normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Short names players type for the common bets. */
const ALIASES: [RegExp, string][] = [
  [/both teams to score/i, 'gg ng'],
  [/over\/under|total|goal line/i, 'ug ukupno'],
  [/handicap/i, 'hc hendikep'],
  [/double chance/i, 'dvostruka sansa 1x x2 12'],
  [
    /^(full time result|fulltime result|match result|full time)$/i,
    '1x2 konacan',
  ],
];

export function marketSearchText(
  bet: Bet | LiveEventBet | MarketCard,
  t: TFunction
): string {
  const name = String(bet.name ?? '');
  const parts = [
    translateBetMarketName(bet as Bet, t),
    name,
    (bet as MarketCard).line ?? '',
    t(`market_group_${marketGroupOf(name)}`),
    ...ALIASES.filter(([re]) => re.test(name)).map(([, words]) => words),
  ];
  return foldSearchText(parts.join(' '));
}

/** The markets whose name holds every word of the query; all if it is blank. */
export function filterMarkets<T extends Bet | LiveEventBet | MarketCard>(
  bets: T[],
  query: string,
  t: TFunction
): T[] {
  const words = foldSearchText(query).split(/\s+/).filter(Boolean);
  if (!words.length) return bets;
  return bets.filter((b) => {
    const text = marketSearchText(b, t);
    return words.every((w) => text.includes(w));
  });
}
